import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Rocket, X, Wand2, Loader2, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export const OnboardingCard = () => {
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [loadingSample, setLoadingSample] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/companies/${company.id}/onboarding`); setData(data); }
    catch (e) { console.error("Failed to load onboarding", e); }
  };
  useEffect(() => {
    if (company) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  if (!data || data.dismissed || data.completed === data.total) return null;

  const dismiss = async () => {
    try { await api.post(`/companies/${company.id}/onboarding/dismiss`); setData((d) => ({ ...d, dismissed: true })); }
    catch { toast.error("Could not dismiss"); }
  };

  const loadSample = async () => {
    setLoadingSample(true);
    try {
      await api.post(`/companies/${company.id}/sample-data`);
      toast.success("Sample metrics, prospects and a draft update loaded");
      load();
      window.location.reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not load sample data"); }
    finally { setLoadingSample(false); }
  };

  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <Card className="mb-6 rounded-xl border border-primary/25 bg-primary/5 p-5" data-testid="onboarding-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground" data-testid="onboarding-title">{data.completed === 0 ? "Get set up: your first investor update" : "Finish setting up"}</h2>
          <span className="text-xs tabular-nums text-muted-foreground" data-testid="onboarding-progress">{data.completed}/{data.total}</span>
        </div>
        <button onClick={dismiss} className="text-muted-foreground hover:text-foreground" data-testid="onboarding-dismiss" aria-label="Dismiss onboarding">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-primary/15">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {data.steps.map((s, i) => (
          <Link key={s.key} to={s.link}
            className={`flex items-start gap-2 rounded-lg border p-2.5 text-xs transition-colors ${s.done ? "border-transparent bg-emerald-50 text-emerald-700" : "bg-card text-foreground hover:border-primary/40"}`}
            data-testid={`onboarding-step-${s.key}`}>
            {s.done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <span className="ff-mono mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-current text-[9px] font-bold">{i + 1}</span>}
            <span className="flex-1"><span className="font-medium">{s.label}</span>{!s.done && s.hint && <span className="mt-0.5 block text-[11px] text-muted-foreground">{s.hint}</span>}</span>
            {!s.done && <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          </Link>
        ))}
      </div>
      {!data.has_metrics && (
        <Button variant="outline" size="sm" className="mt-4 gap-1.5" onClick={loadSample} disabled={loadingSample} data-testid="onboarding-sample-btn">
          {loadingSample ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          Load sample data to explore
        </Button>
      )}
    </Card>
  );
};
